// Projekte: every active project with your time, agent time and the billed (merged) figure for today or the week.
import { useMemo, useState } from 'react';
import { dayRange, weekRange } from '../lib/calendar.ts';
import { navigate } from '../lib/router.ts';
import { isOwner, activeProjects, client, runningEntry } from '../model/data.ts';
import * as m from '../model/mutations.ts';
import { buildSnapshot } from '../model/snapshot.ts';
import { Banner, EmptyState, Figure, Loading, ScreenHeader } from '../components/common.tsx';
import { RowButton } from '../components/pickers.tsx';
import { useFmt, useMedia, useNow, useRange, useStore } from '../store/hooks.tsx';
import { Button, ProjectRow, SegmentedControl } from '../ui/ds.tsx';
import { Icon } from '../ui/Icon.tsx';

export function Projects() {
  const store = useStore();
  const t = store.t;
  const fmt = useFmt();
  const now = useNow(1000);
  const [period, setPeriod] = useState<'today' | 'week'>('week');
  const [showArchive, setShowArchive] = useState(false);
  const range = useMemo(() => (period === 'today' ? dayRange(now, store.timeZone) : weekRange(now, store.timeZone)), [period, Math.floor(now / 60_000), store.timeZone]);
  const loaded = useRange(range);
  const snap = loaded.data ? buildSnapshot(store.ws, loaded.data, range, now) : undefined;
  const running = loaded.data ? runningEntry(loaded.data) : undefined;
  const projects = activeProjects(store.ws);
  const archived = store.ws.projects.filter((p) => p.archivedAt !== null && p.deletedAt === null);
  const compact = useMedia('(max-width: 860px)');
  return (
    <div className="screen">
      <ScreenHeader title={t('Projekte')} subtitle={`${t('%lld aktive Projekte', projects.length)} · ${t('Woche %lld', fmt.week(now))}`}>
        <SegmentedControl label={t('Zeitraum')} value={period} onChange={setPeriod} options={[{ value: 'today', label: t('Heute') }, { value: 'week', label: t('Woche') }]} />
        {isOwner(store.ws) ? <Button icon="plus" onClick={() => store.openSheet({ kind: 'project', projectId: null })}>{t('Projekt')}</Button> : null}
      </ScreenHeader>
      {projects.length === 0 ? (
        <EmptyState icon="folder" title={t('Noch keine Projekte')} message={t('Lege ein Projekt an, damit Tellary deine Zeit und die deiner Agents zuordnen und abrechnen kann.')}>
          {isOwner(store.ws) ? <Button icon="plus" onClick={() => store.openSheet({ kind: 'project', projectId: null })}>{t('Projekt anlegen')}</Button> : null}
        </EmptyState>
      ) : !snap ? (loaded.error ? <Banner tone="danger">{loaded.error}</Banner> : <Loading label={t('Lädt …')} />) : (
        <>
          <div className="tl-card table">
            {compact ? null : (
              <div className="table__head projects-grid" aria-hidden="true">
                <span>{t('Projekt')}</span><span>{t('Eigene / Agents')}</span>
                <span className="table__num">{t('Eigene')}</span><span className="table__num">{t('Agents')}</span><span className="table__num">{t('Abgerechnet')}</span>
              </div>
            )}
            {projects.map((p) => {
              const totals = snap.byProject.get(p.id);
              return (
                <RowButton key={p.id} onClick={() => navigate({ name: 'project', id: p.id })} label={p.name}>
                  <ProjectRow compact={compact} name={p.name} client={client(store.ws, p.clientId)?.name} color={p.colorSlot} humanMs={totals?.humanMs ?? 0}
                    agentMs={totals?.agentMs ?? 0} mergedMs={totals?.mergedMs ?? 0} running={running?.projectId === p.id}
                    amount={p.billable && p.rateCents !== null ? fmt.money(totals?.amountCents ?? 0, p.currency) : undefined} />
                </RowButton>
              );
            })}
          </div>
          <div className="figures">
            <Figure label={t('Abgerechnet')} value={fmt.duration(snap.mergedMs)} strong />
            <Figure label={t('Summe')} value={fmt.duration(snap.rawMs)} />
            <Figure label={t('Überschneidung 1× gezählt')} value={fmt.duration(snap.overlapMs)} />
            <Figure label={t('Abrechenbar')} value={fmt.money(snap.amountCents)} strong />
          </div>
        </>
      )}
      {archived.length > 0 ? (
        <div className="stack">
          <Button variant="plain" size="sm" className="archive-toggle" onClick={() => setShowArchive(!showArchive)} aria-expanded={showArchive}>
            <Icon name={showArchive ? 'chevronDown' : 'chevron'} size={14} />{t('Archiv (%lld)', archived.length)}
          </Button>
          {showArchive ? (
            <div className="tl-card table">
              {archived.map((p) => (
                <div key={p.id} className="table__row archive-row">
                  <span className="table__cell"><span className="tl-dot" style={{ background: `var(--project-${p.colorSlot})` }} /><span className="clip">{p.name}</span></span>
                  <Button variant="plain" size="sm" onClick={() => navigate({ name: 'project', id: p.id })}>{t('Öffnen')}</Button>
                  <Button size="sm" onClick={() => void store.apply(m.archiveProject(p, false, store.clock()))}>{t('Aus dem Archiv holen')}</Button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
